/**
 * Appointment Booking Component JavaScript
 * Handles view transitions, cookie management, and user interactions
 */

(function () {
    'use strict';

    // Cookie utility functions
    const CookieManager = {
        /**
         * Set a cookie with name, value, and optional expiration days
         * @param {string} name - Cookie name
         * @param {string} value - Cookie value
         * @param {number} days - Expiration days (default: 30)
         */
        set: function (name, value, days = 30) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            const expires = 'expires=' + date.toUTCString();
            document.cookie = name + '=' + encodeURIComponent(value) + ';' + expires + ';path=/';
        },

        /**
         * Get cookie value by name
         * @param {string} name - Cookie name
         * @return {string|null} Cookie value or null if not found
         */
        get: function (name) {
            const nameEQ = name + '=';
            const cookies = document.cookie.split(';');

            for (let i = 0; i < cookies.length; i++) {
                let cookie = cookies[i];
                while (cookie.charAt(0) === ' ') {
                    cookie = cookie.substring(1, cookie.length);
                }
                if (cookie.indexOf(nameEQ) === 0) {
                    return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
                }
            }
            return null;
        },

        /**
         * Delete cookie by name
         * @param {string} name - Cookie name
         */
        delete: function (name) {
            document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        }
    };

    // Main component class
    const AppointmentBooking = {
        // Cookie name for storing appointment selection
        COOKIE_NAME: 'bbj_appointment_selection',

        // Step constants for navigation
        STEPS: {
            APPOINTMENT_SELECTION: 'appointmentSelection',
            CONTACT_FORM: 'contactForm',
            CONFIRM_APPOINTMENT: 'confirmAppointment',
            APPOINTMENT_CONFIRMED: 'appointmentConfirmed',
            CLIENT_CAPTURE: 'ClientCapture'
        },

        // View elements
        elements: {
            selectionView: null,
            appointmentNoteView: null,
            appointmentNoteTextWatches: null,
            appointmentNoteTextServicing: null,
            storeSelectionView: null,
            storeStateSelect: null,
            storeAddressesContainer: null,
            appointmentDateTimeView: null,
            appointmentContactInfoView: null,
            appointmentConfirmationView: null,
            appointmentRescheduleView: null,
            appointmentOption1: null,
            appointmentOption2: null,
            backButton: null,
            backButtonStoreSelection: null,
            backButtonDateTime: null,
            backButtonContactInfo: null,
            understandButton: null,
            nextButton: null
        },

        // Currently selected appointment type
        selectedAppointmentType: null,
        selectedStoreId: null,
        selectedStoreTimezoneId: null,
        storesByState: {},
        storesLoaded: false,
        storesRequestPromise: null,
        currentAppointmentDatesContext: null,
        selectedDateTime: null,
        rescheduleAppointmentData: null,
        currentRescheduleContext: null,
        selectedRescheduleDateTime: null,
        heldAppointmentData: null,
        confirmationData: null,
        preventBackNavigation: false,

        // Resets date/time selection state in DateTime view (overridden during DateTime init)
        resetDateTimeSelection: function () {},

        // Date/Time error helpers (overridden during DateTime init)
        showDateTimeError: function () {},
        clearDateTimeError: function () {},

        // Reschedule view helpers (overridden during reschedule init)
        applyAppointmentDatesToRescheduleView: function () {},
        resetRescheduleSelection: function () {},

        trackEvent: function (eventName) {
            if (!eventName) {
                return;
            }

            if (window._satellite && typeof window._satellite.track === 'function') {
                window._satellite.track(eventName);
            }
        },

        /**
         * Initialize the component
         */
        init: function () {
            this.cacheElements();
            this.bindEvents();
            this.elements.selectionView.classList.add('hidden');
            this.elements.selectionView.classList.remove('hidden');
            this.restoreState();
        },

        /**
         * Cache DOM elements for performance
         */
        cacheElements: function () {
            this.elements.selectionView = document.getElementById('appointmentPurposeView');
            this.elements.appointmentNoteView = document.getElementById('appointmentNoteView');
            this.elements.appointmentNoteTextWatches = document.getElementById('appointmentNoteTextWatches');
            this.elements.appointmentNoteTextServicing = document.getElementById('appointmentNoteTextServicing');
            this.elements.storeSelectionView = document.getElementById('storeSelectionView');
            this.elements.storeStateSelect = document.getElementById('storeStateSelect');
            this.elements.storeAddressesContainer = this.elements.storeSelectionView ? this.elements.storeSelectionView.querySelector('[store-locator-addresses]') : null;
            this.elements.appointmentDateTimeView = document.getElementById('appointmentDateTimeView');
            this.elements.appointmentContactInfoView = document.getElementById('appointmentContactInfoView');
            this.elements.appointmentConfirmationView = document.getElementById('appointmentConfirmationView');
            this.elements.appointmentRescheduleView = document.getElementById('appointmentRescheduleView');
            this.elements.appointmentOption1 = document.getElementById('option1');
            this.elements.appointmentOption2 = document.getElementById('option2');
            this.elements.backButton = document.getElementById('backButton');
            this.elements.backButtonStoreSelection = document.getElementById('backButtonStoreSelection');
            this.elements.backButtonDateTime = document.getElementById('backButtonDateTime');
            this.elements.backButtonContactInfo = document.getElementById('backButtonContactInfo');
            this.elements.understandButton = document.getElementById('understandButton');
            this.elements.nextButton = document.getElementById('nextButton');
        },

        /**
         * Bind event listeners
         */
        bindEvents: function () {
            var tryLoadAppointmentDates = () => {
                this.showLoader();
                this.loadAppointmentDates()
                    .then((appointmentDatesData) => {
                        this.currentAppointmentDatesContext = appointmentDatesData;
                        if (typeof this.applyAppointmentDatesToDateTimeView === 'function') {
                            this.applyAppointmentDatesToDateTimeView(appointmentDatesData);
                        }
                        this.elements.storeSelectionView.classList.add('hidden');
                        this.showDateTime();
                        this.scrollView('appointmentDateTimeView');
                    })
                    .catch(() => {
                        this.showDateTimeError('Unable to load dates. Please try again.');
                    })
                    .finally(() => {
                        this.hideLoader();
                    });
            };

            // Appointment option selection
            this.elements.appointmentOption1.addEventListener('click', (e) => {
                this.selectAppointmentOption(this.elements.appointmentOption1, 1);
            });
            this.elements.appointmentOption2.addEventListener('click', (e) => {
                this.selectAppointmentOption(this.elements.appointmentOption2, 2);
            });

            // Back button
            if (this.elements.backButton) {
                this.elements.backButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.goBack();
                });
            }

            // Back button store selection
            if (this.elements.backButtonStoreSelection) {
                this.elements.backButtonStoreSelection.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.resetStoreCardSelection();
                    this.goBackToNote();
                });
            }

            // Back button DateTime
            if (this.elements.backButtonDateTime) {
                this.elements.backButtonDateTime.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.resetDateTimeSelection();
                    this.removeAppointmentInformationScript();
                    this.goBackToStoreSelection();
                });
            }

            // Back button ContactInfo
            if (this.elements.backButtonContactInfo) {
                this.elements.backButtonContactInfo.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.showLoader();
                    this.releaseHeldAppointment()
                        .catch((error) => {
                            //this.showDateTimeError((error && error.message) || 'Unable to release appointment. Please try again.');
                        })
                        .finally(() => {
                            this.hideLoader();
                            this.goBackToDateTime();
                        });
                });
            }

            // Understand button
            if (this.elements.understandButton) {
                this.elements.understandButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.loadStoresData()
                        .then(() => {
                            this.elements.appointmentNoteView.classList.add('hidden');
                            this.showStoreSelection();
                            this.scrollView('storeSelectionView');
                        })
                        .catch(() => {
                            this.showDateTimeError('Unable to load stores. Please try again.');
                        });

                });
            }
            // Next button (Store Selection -> DateTime)
            if (this.elements.nextButton) {
                this.elements.nextButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (!this.hasSelectedStoreCard()) {
                        return;
                    }

                    tryLoadAppointmentDates();
                });
            }

            // Store card selection
            if (this.elements.storeSelectionView) {
                this.elements.storeSelectionView.addEventListener('click', (e) => {
                    var card = e.target.closest('.rolex-store-card');
                    if (!card || !this.elements.storeSelectionView.contains(card)) {
                        return;
                    }

                    var storeCards = this.elements.storeSelectionView.querySelectorAll('.rolex-store-card');
                    storeCards.forEach(function (storeCard) {
                        storeCard.classList.remove('selected');
                    });

                    card.classList.add('selected');
                    this.selectedStoreId = card.getAttribute('data-store-id') || null;
                    this.selectedStoreTimezoneId = card.getAttribute('data-timezone-id') || null;
                    this.updateStoreSelectionButtonState();
                });
            }

            // Reset selected store card when state/country dropdown changes
            if (this.elements.storeStateSelect) {
                this.elements.storeStateSelect.addEventListener('change', () => {
                    this.resetStoreCardSelection();
                    this.renderStoresByState(this.elements.storeStateSelect.value);
                });
            }

            this.updateStoreSelectionButtonState();

            // Handle browser back button
            window.addEventListener('popstate', (e) => {
                if (this.preventBackNavigation) {
                    this.showView('selection');
                    this.scrollView('appointmentPurposeView');

                    if (window.history && window.history.pushState) {
                        window.history.pushState({
                            view: 'selection'
                        }, 'Appointment Selection', '');
                    }
                    return;
                }

                var targetView = e.state && e.state.view ? e.state.view : 'selection';
                var currentView = this.getCurrentVisibleView();

                if (currentView === 'confirmation') {
                    this.resetAllBookingState();
                    this.showView('selection');
                    this.scrollView('appointmentPurposeView');
                    return;
                }

                if (currentView === 'contactInfo') {
                    this.showLoader();
                    this.releaseHeldAppointment()
                        .catch(() => {})
                        .finally(() => {
                            this.hideLoader();
                            this.showView(targetView);
                        });
                    return;
                }

                this.showView(targetView);
            });
        },

        getCurrentVisibleView: function () {
            if (this.elements.appointmentConfirmationView && !this.elements.appointmentConfirmationView.classList.contains('hidden')) {
                return 'confirmation';
            }

            if (this.elements.appointmentRescheduleView && !this.elements.appointmentRescheduleView.classList.contains('hidden')) {
                return 'reschedule';
            }

            if (this.elements.appointmentContactInfoView && !this.elements.appointmentContactInfoView.classList.contains('hidden')) {
                return 'contactInfo';
            }

            if (this.elements.appointmentDateTimeView && !this.elements.appointmentDateTimeView.classList.contains('hidden')) {
                return 'dateTime';
            }

            if (this.elements.storeSelectionView && !this.elements.storeSelectionView.classList.contains('hidden')) {
                return 'storeSelection';
            }

            if (this.elements.appointmentNoteView && !this.elements.appointmentNoteView.classList.contains('hidden')) {
                return 'note';
            }

            return 'selection';
        },

        /**
         * Check if any store card is selected
         * @return {boolean} True when at least one store card has selected class
         */
        hasSelectedStoreCard: function () {
            if (!this.elements.storeSelectionView) {
                return false;
            }

            return !!this.elements.storeSelectionView.querySelector('.rolex-store-card.selected');
        },

        /**
         * Toggle store selection next button based on selected card
         */
        updateStoreSelectionButtonState: function () {
            if (!this.elements.nextButton) {
                return;
            }

            if (this.hasSelectedStoreCard()) {
                this.elements.nextButton.removeAttribute('disabled');
            } else {
                this.elements.nextButton.setAttribute('disabled', 'true');
            }
        },

        /**
         * Clear selected class from all store cards and disable Next
         */
        resetStoreCardSelection: function () {
            if (!this.elements.storeSelectionView) {
                return;
            }

            var storeCards = this.elements.storeSelectionView.querySelectorAll('.rolex-store-card');
            storeCards.forEach(function (storeCard) {
                storeCard.classList.remove('selected');
            });

            this.selectedStoreId = null;
            this.selectedStoreTimezoneId = null;
            this.updateStoreSelectionButtonState();
        },

        clearAppointmentTypeSelectionUI: function () {
            if (this.elements.appointmentOption1) {
                this.elements.appointmentOption1.classList.remove('selected');
            }

            if (this.elements.appointmentOption2) {
                this.elements.appointmentOption2.classList.remove('selected');
            }
        },

        resetContactFormState: function () {
            var form = document.getElementById('appointmentContactForm');
            if (form && typeof form.reset === 'function') {
                form.reset();
            }

            if (!this.elements.appointmentContactInfoView) {
                return;
            }

            var invalidFields = this.elements.appointmentContactInfoView.querySelectorAll('.invalid-field');
            invalidFields.forEach(function (node) {
                node.classList.remove('invalid-field');
            });

            var termsError = this.elements.appointmentContactInfoView.querySelector('#contactTermsError');
            if (termsError) {
                termsError.style.display = 'none';
            }

            var marketingError = this.elements.appointmentContactInfoView.querySelector('#contactMarketingError');
            if (marketingError) {
                marketingError.style.display = 'none';
            }
        },

        removeAppointmentInformationScript: function () {
            var appointmentInformationScript = document.getElementById('appointmentInformationScript');
            if (appointmentInformationScript && appointmentInformationScript.parentNode) {
                appointmentInformationScript.parentNode.removeChild(appointmentInformationScript);
            }

            if (window.appointmentInformation) {
                window.appointmentInformation = '';
            }
        },

        resetAllBookingState: function () {
            CookieManager.delete(this.COOKIE_NAME);
            this.selectedAppointmentType = null;
            this.selectedStoreId = null;
            this.selectedStoreTimezoneId = null;
            this.currentAppointmentDatesContext = null;
            this.selectedDateTime = null;
            this.rescheduleAppointmentData = null;
            this.currentRescheduleContext = null;
            this.selectedRescheduleDateTime = null;
            this.heldAppointmentData = null;
            this.confirmationData = null;

            this.clearAppointmentTypeSelectionUI();
            this.resetStoreCardSelection();
            this.resetDateTimeSelection();
            this.resetRescheduleSelection();
            this.clearDateTimeError();
            this.resetContactFormState();

            this.removeAppointmentInformationScript();
        },

        showLoader: function () {
            if (window.$ && typeof $.spinner === 'function') {
                $.spinner().start();
            }
        },

        hideLoader: function () {
            if (window.$ && typeof $.spinner === 'function') {
                $.spinner().stop();
            }
        },

        mapStoresListEntry: function (storeEntry) {
            var formattedAddress = storeEntry.formattedAddress || '';
            var formattedParts = formattedAddress
                .split(',')
                .map(function (part) { return (part || '').trim(); })
                .filter(function (part) { return !!part; });

            var addressLine = [storeEntry.address1 || '', storeEntry.address2 || '']
                .filter(function (part) { return !!part; })
                .join(', ');

            var cityLine = [storeEntry.city || '', storeEntry.stateCode || '', storeEntry.postalCode || '']
                .filter(function (part) { return !!part; })
                .join(', ');

            if (!addressLine && formattedParts.length) {
                if (formattedParts.length >= 5) {
                    addressLine = formattedParts.slice(0, 2).join(', ');
                } else {
                    addressLine = formattedParts[0] || '';
                }
            }

            if (!cityLine && formattedParts.length >= 4) {
                cityLine = formattedParts.slice(2, 5).join(', ');
            }

            var country = storeEntry.countryDisplayValue || storeEntry.countryCode || '';
            if (!country && formattedParts.length) {
                country = formattedParts[formattedParts.length - 1] || '';
            }
            
            var brandLabel = '';
            if(storeEntry.isRolex === null || storeEntry.isRolex === undefined) {
                 brandLabel = 'Official Rolex Retailer';
            } else {
                brandLabel = storeEntry.isRolex ? 'Official Rolex Jeweler' : 'Official Ben Bridge Jeweler';
            }

            return {
                id: storeEntry.ID || storeEntry.id || '',
                brandLabel: brandLabel,
                title: storeEntry.name || '',
                address1: addressLine,
                city: cityLine,
                country: country,
                timezoneId: storeEntry.timezoneId || storeEntry.timezone || ''
            };
        },

        normalizeStoresByState: function (apiResponse) {
            var storesByState = {};

            if (apiResponse && apiResponse.stores && Array.isArray(apiResponse.stores.states)) {
                apiResponse.stores.states.forEach((stateEntry) => {
                    if (!stateEntry || !stateEntry.label || !Array.isArray(stateEntry.stores)) {
                        return;
                    }

                    storesByState[stateEntry.label] = stateEntry.stores.map((storeEntry) => this.mapStoresListEntry(storeEntry));
                });
            }

            if (apiResponse && apiResponse.state && typeof apiResponse.state === 'object' && !Array.isArray(apiResponse.state)) {
                Object.keys(apiResponse.state).forEach(function (stateName) {
                    var stores = apiResponse.state[stateName];
                    if (Array.isArray(stores)) {
                        storesByState[stateName] = stores.map((storeEntry) => this.mapStoresListEntry(storeEntry));
                    }
                }, this);
            }

            if (apiResponse && Array.isArray(apiResponse.locations)) {
                storesByState['All Stores'] = apiResponse.locations.map((locationEntry) => this.mapStoresListEntry(locationEntry));
            }

            return storesByState;
        },

        fetchStoresData: function () {
            var siteIdNode = document.querySelector('.site-id');
            var siteId = siteIdNode ? String(siteIdNode.value || '').trim() : '';
            var storesListUrl = document.querySelector('.store-list') ? document.querySelector('.store-list').value : '';
            var bookingLocationsUrl = document.querySelector('.booking-locations') ? document.querySelector('.booking-locations').value : '';
            var url = siteId === 'BenBridge' ? storesListUrl : (bookingLocationsUrl || storesListUrl);

            return fetch(url, {
                method: 'GET',
                headers: {
                    Accept: 'application/json'
                }
            }).then(function (response) {
                if (!response.ok) {
                    throw new Error('Failed to load stores');
                }
                return response.json();
            });
        },

        populateStateSelect: function () {
            if (!this.elements.storeStateSelect) {
                return;
            }

            var options = ['<option value="">Stores in State</option>'];
            Object.keys(this.storesByState).forEach(function (stateName) {
                options.push('<option value="' + stateName + '">' + stateName + '</option>');
            });

            this.elements.storeStateSelect.innerHTML = options.join('');
            this.elements.storeStateSelect.value = '';
        },

        escapeHtml: function (value) {
            if (value === null || value === undefined) {
                return '';
            }

            return String(value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },

        createStoreCardMarkup: function (store) {
            var brandLabel = this.escapeHtml(store.brandLabel || 'Official Rolex Jeweler');
            var title = this.escapeHtml(store.title || store.name || 'Rolex Boutique');
            var address1 = this.escapeHtml(store.address1 || '');
            var city = this.escapeHtml(store.city || '');
            var country = this.escapeHtml(store.country || '');
            var storeId = this.escapeHtml(store.id || '');
            var timezoneId = this.escapeHtml(store.timezoneId || '');

            return '<div class="rolex-store-card" data-store-id="' + storeId + '" data-timezone-id="' + timezoneId + '" google-map-location="" style="width: auto; cursor: pointer;">' +
                '<div class="holder">' +
                    '<div class="info">' +
                        '<div class="card-address-header">' +
                            '<p class="brand-label rolex-v7-fixed16-light">' + brandLabel + '</p>' +
                            '<div class="title rolex-v7-body20-bold pb-3"><span>' + title + '</span></div>' +
                            '<div class="address rolex-v7-fixed16-light">' +
                                '<p>' + address1 + '</p>' +
                                '<p>' + city + '</p>' +
                                '<p>' + country + '</p>' +
                            '</div>' +
                        '</div>' +
                    '</div>' +
                '</div>' +
            '</div>';
        },

        renderStoresByState: function (selectedState) {
            if (!this.elements.storeAddressesContainer) {
                return;
            }

            var stores = [];
            if (selectedState) {
                stores = this.storesByState[selectedState] || [];
            } else {
                Object.keys(this.storesByState).forEach((stateName) => {
                    stores = stores.concat(this.storesByState[stateName] || []);
                });
            }

            if (!stores.length) {
                this.elements.storeAddressesContainer.innerHTML = '<p class="rolex-v7-fixed16-light">No stores found.</p>';
                return;
            }

            var cardsMarkup = stores.map((store) => this.createStoreCardMarkup(store)).join('');
            this.elements.storeAddressesContainer.innerHTML = cardsMarkup;
            this.updateStoreSelectionButtonState();
        },

        loadStoresData: function () {
            this.resetStoreCardSelection();

            if (this.elements.storeAddressesContainer) {
                this.elements.storeAddressesContainer.innerHTML = '';
            }

            // Use cached stores after the first successful load.
            if (this.storesLoaded && Object.keys(this.storesByState).length) {
                this.populateStateSelect();
                this.renderStoresByState('');
                return Promise.resolve(this.storesByState);
            }

            this.showLoader();

            if (!this.storesRequestPromise) {
                this.storesRequestPromise = this.fetchStoresData()
                    .then((responseData) => {
                        this.storesByState = this.normalizeStoresByState(responseData);
                    })
                    .finally(() => {
                        this.storesLoaded = true;
                        this.storesRequestPromise = null;
                    });
            }

            return this.storesRequestPromise
                .then(() => {
                    this.populateStateSelect();
                    this.renderStoresByState('');
                })
                .finally(() => {
                    this.hideLoader();
                });
        },

        getSelectedAppointmentTypePayload: function () {
            var selected = this.selectedAppointmentType;

            if (!selected) {
                return { id: '', label: '' };
            }

            if (typeof selected === 'string') {
                try {
                    selected = JSON.parse(selected);
                } catch (e) {
                    return { id: '', label: '' };
                }
            }

            return {
                id: selected.id || '',
                label: selected.label || ''
            };
        },

        getAppointmentDatesRequestPayload: function () {
            var selectedType = this.getSelectedAppointmentTypePayload();
            return {
                selectedAppointmentType: selectedType,
                selectedStoreId: this.selectedStoreId || '',
                timezoneId: this.selectedStoreTimezoneId || ''
            };
        },

        normalizeStartTime: function (timeStr) {
            if (!timeStr) {
                return '';
            }

            var value = String(timeStr);
            if (value.length >= 5) {
                return value.slice(0, 5);
            }

            return value;
        },

        buildCreateAppointmentPayload: function () {
            var dateContext = this.currentAppointmentDatesContext || {};
            var selectedType = this.getSelectedAppointmentTypePayload();
            var dateTime = this.selectedDateTime || {};

            return {
                locationId: dateContext.locationId || '',
                productId: dateContext.productId || selectedType.label || '',
                startTime: this.normalizeStartTime(dateTime.startTime || ''),
                date: dateTime.date || '',
            };
        },

        createAppointmentApi: function (payload) {

            var url = document.querySelector('.booking-create-appointment') ? document.querySelector('.booking-create-appointment').value : '';

            return fetch(url, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload || {})
            }).then(function (response) {
                if (!response.ok) {
                    return response.text().then(function (rawText) {
                        var message = 'Failed to create appointment';

                        if (rawText) {
                            try {
                                var parsed = JSON.parse(rawText);
                                message = parsed.message || parsed.errorMessage || parsed.error || rawText;
                            } catch (e) {
                                message = rawText;
                            }
                        }

                        throw new Error(message);
                    });
                }
                return response.json();
            });
        },

        releaseAppointmentApi: function (appointmentId) {
            var url = document.querySelector('.booking-release-appointment') ? document.querySelector('.booking-release-appointment').value : '';
            var params = new URLSearchParams({
                appointmentId: appointmentId || ''
            });

            return fetch(url + '?' + params.toString(), {
                method: 'GET',
                headers: {
                    Accept: 'application/json'
                }
            }).then(function (response) {
                if (!response.ok) {
                    return response.text().then(function (rawText) {
                        var message = 'Failed to release appointment';

                        if (rawText) {
                            try {
                                var parsed = JSON.parse(rawText);
                                message = parsed.message || parsed.errorMessage || parsed.error || rawText;
                            } catch (e) {
                                message = rawText;
                            }
                        }

                        throw new Error(message);
                    });
                }

                return response.json();
            });
        },

        holdSelectedAppointment: function () {
            var payload = this.buildCreateAppointmentPayload();
            if (!payload.locationId || !payload.productId || !payload.startTime) {
                return Promise.reject(new Error('Missing hold payload fields'));
            }

            return this.createAppointmentApi(payload)
                .then((responseData) => {
                    if (!responseData || !responseData.appointmentId) {
                        let errorMessage = 'Unable to hold selected slot. Please try another slot.';

                        if (responseData?.message) {
                            try {
                                const messageObj = JSON.parse(responseData.message);
                                errorMessage = messageObj.error?.message || errorMessage;
                            } catch (e) {
                                errorMessage = responseData.message;
                            }
                        }

                        throw new Error(errorMessage);
                    }

                    this.heldAppointmentData = responseData;
                    return this.heldAppointmentData;
                });
        },

        releaseHeldAppointment: function () {
            var appointmentId = this.heldAppointmentData && this.heldAppointmentData.appointmentId;

            if (!appointmentId) {
                this.heldAppointmentData = null;
                return Promise.resolve();
            }

            return this.releaseAppointmentApi(appointmentId)
                .then((responseData) => {
                    if (!responseData || responseData.message !== 'No Content') {
                        throw new Error((responseData && responseData.message) || 'Unable to release appointment. Please try again.');
                    }

                    this.heldAppointmentData = null;
                    return responseData;
                });
        },

        fetchAppointmentById: function (appointmentId) {
            var url = document.querySelector('.booking-get-appointment') ? document.querySelector('.booking-get-appointment').value : '';
            var params = new URLSearchParams({
                appointmentId: appointmentId || ''
            });

            return fetch(url + '?' + params.toString(), {
                method: 'GET',
                headers: {
                    Accept: 'application/json'
                }
            }).then(function (response) {
                if (!response.ok) {
                    return response.text().then(function (rawText) {
                        var message = 'Unable to get appointment details.';

                        if (rawText) {
                            try {
                                var parsed = JSON.parse(rawText);
                                message = parsed.message || parsed.errorMessage || parsed.error || rawText;
                            } catch (e) {
                                message = rawText;
                            }
                        }

                        throw new Error(message);
                    });
                }

                return response.json();
            }).then(function (responseData) {
                if (responseData && responseData.error) {
                    var errorMessage = 'Unable to get appointment details.';
                    if (responseData.message) {
                        try {
                            var parsedMessage = JSON.parse(responseData.message);
                            errorMessage = (parsedMessage.error && parsedMessage.error.message) || responseData.message;
                        } catch (e) {
                            errorMessage = responseData.message;
                        }
                    }

                    throw new Error(errorMessage);
                }

                return responseData || {};
            });
        },

        fetchAppointmentDatesByContext: function (context) {
            var requestContext = context || {};
            var url = document.querySelector('.booking-availability-dates') ? document.querySelector('.booking-availability-dates').value : '';

            var productId = encodeURIComponent(requestContext.productId || '');
            var locationId = encodeURIComponent(requestContext.locationId || '');
            var timeZoneId = encodeURIComponent(requestContext.timezoneId || '');
            var params = '?productId=' + productId + '&locationId=' + locationId + '&timezoneId=' + timeZoneId;

            return fetch(url + params, {
                method: 'GET',
                headers: {
                    Accept: 'application/json'
                }
            }).then(function (response) {
                return response.json().then(function (data) {
                    if (!response.ok) {
                        let errorMessage = 'Failed to load appointment dates. Please try again.';

                        if (data.message) {
                            try {
                                const messageObj = JSON.parse(data.message);
                                errorMessage = messageObj.error?.message || errorMessage;
                            } catch (e) {
                                errorMessage = data.message;
                            }
                        }
                        throw new Error(errorMessage);
                    }
                    return data;
                });
            });
        },

        loadAppointmentDatesByContext: function (context) {
            return this.fetchAppointmentDatesByContext(context)
                .then((responseData) => this.normalizeAppointmentDatesResponse(responseData));
        },

        fetchAppointmentSlotsByContext: function (selectedDate, context) {
            var requestContext = context || {};
            var url = document.querySelector('.booking-availability-slots') ? document.querySelector('.booking-availability-slots').value : '';
            var params = new URLSearchParams({
                locationId: requestContext.locationId || '',
                timezoneId: requestContext.timezoneId || '',
                date: selectedDate || '',
                productId: requestContext.productId || ''
            });

            return fetch(url + '?' + params.toString(), {
                method: 'GET',
                headers: {
                    Accept: 'application/json'
                }
            }).then(function (response) {
                return response.json().then(function (data) {
                    if (!response.ok) {
                        let errorMessage = 'Failed to load appointment slots. Please try again.';

                        if (data.message) {
                            try {
                                const messageObj = JSON.parse(data.message);
                                errorMessage = messageObj.error?.message || errorMessage;
                            } catch (e) {
                                errorMessage = data.message;
                            }
                        }
                        throw new Error(errorMessage);
                    }
                    return data;
                });
            });
        },

        loadAppointmentSlotsByContext: function (selectedDate, context) {
            return this.fetchAppointmentSlotsByContext(selectedDate, context)
                .then((responseData) => this.normalizeAppointmentSlotsResponse(responseData));
        },

        fetchAppointmentConfirm: function (payload) {
            return fetch(document.querySelector('.booking-confirm-appointment') ? document.querySelector('.booking-confirm-appointment').value : '', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            }).then(function (response) {
                return response.json().then(function (data) {
                    if (!response.ok) {
                        let errorMessage = 'Failed to confirm appointment. Please try again.';

                        if (data.message) {
                            try {
                                const messageObj = JSON.parse(data.message);
                                errorMessage = messageObj.error?.message || errorMessage;
                            } catch (e) {
                                errorMessage = data.message;
                            }
                        }

                        throw new Error(errorMessage);
                    }

                    return data;
                });
            });
        },

        fetchAppointmentReschedule: function (payload) {
            var endpointNode = document.querySelector('.reschedule-appointment-url') || document.querySelector('.booking-reschedule-appointment');
            var url = endpointNode ? endpointNode.value : '';

            return fetch(url, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload || {})
            }).then(function (response) {
                return response.text().then(function (text) {
                    var data = {};
                    if (text) {
                        try {
                            data = JSON.parse(text);
                        } catch (e) {
                            data = {
                                message: text
                            };
                        }
                    }

                    if (!response.ok) {
                        throw new Error(data.message || 'Failed to reschedule appointment. Please try again.');
                    }

                    if (data && data.error) {
                        throw new Error(data.message || 'Failed to reschedule appointment. Please try again.');
                    }

                    return data;
                });
            });
        },

        fetchAppointmentCancel: function (payload) {
            var endpointNode = document.querySelector('.cancel-appointment-url') || document.querySelector('.booking-cancel-appointment');
            var url = endpointNode ? endpointNode.value : '';

            return fetch(url, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload || {})
            }).then(function (response) {
                return response.text().then(function (text) {
                    var data = {};
                    if (text) {
                        try {
                            data = JSON.parse(text);
                        } catch (e) {
                            data = {
                                message: text
                            };
                        }
                    }

                    if (!response.ok) {
                        throw new Error(data.message || 'Failed to cancel appointment. Please try again.');
                    }

                    if (data && data.error) {
                        throw new Error(data.message || 'Failed to cancel appointment. Please try again.');
                    }

                    return data;
                });
            });
        },

        buildReschedulePayload: function () {
            var selectedDateTime = this.selectedRescheduleDateTime || {};
            var context = this.currentRescheduleContext || {};
            var appointment = this.rescheduleAppointmentData || {};

            return {
                appointmentId: appointment.id || appointment.appointmentId || (this.confirmationData && this.confirmationData.appointmentId) || '',
                locationId: context.locationId || appointment.locationId || '',
                date: selectedDateTime.date || '',
                startTime: this.normalizeStartTime(selectedDateTime.startTime || '')
            };
        },

        buildCancelPayload: function () {
            var appointment = this.rescheduleAppointmentData || {};

            return {
                appointmentId: appointment.id || appointment.appointmentId || (this.confirmationData && this.confirmationData.appointmentId) || '',
                cancellationReason: 'Cancelled by user from reschedule flow'
            };
        },

        getSelectedStoreData: function () {
            if (!this.selectedStoreId) {
                return null;
            }

            var stateNames = Object.keys(this.storesByState || {});
            for (var i = 0; i < stateNames.length; i++) {
                var stateStores = this.storesByState[stateNames[i]] || [];
                for (var j = 0; j < stateStores.length; j++) {
                    var store = stateStores[j];
                    if (store && store.id === this.selectedStoreId) {
                        return store;
                    }
                }
            }

            return null;
        },

        populateContactStoreDetails: function () {
            var contactView = this.elements.appointmentContactInfoView;
            if (!contactView) {
                return;
            }

            var staticBlock = contactView.querySelector('.contact-static-block');
            if (!staticBlock) {
                return;
            }

            var valueNodes = staticBlock.querySelectorAll('.contact-static-value');
            if (!valueNodes.length) {
                return;
            }

            var selectedStore = this.getSelectedStoreData();
            var storeCountry = selectedStore && selectedStore.country ? selectedStore.country : '';
            var storeCity = selectedStore && selectedStore.city ? selectedStore.city : '';
            var storeAddress = selectedStore && selectedStore.address1 ? selectedStore.address1 : '';

            if (valueNodes[0]) {
                valueNodes[0].textContent = storeCountry;
            }
            if (valueNodes[1]) {
                valueNodes[1].textContent = storeCity;
            }
            if (valueNodes[2]) {
                valueNodes[2].textContent = storeAddress;
            }
        },

        fetchAppointmentDates: function () {
            var payload = this.getAppointmentDatesRequestPayload();
            var url = document.querySelector('.booking-availability-dates') ? document.querySelector('.booking-availability-dates').value : '';
            
            var productId = encodeURIComponent(payload.selectedAppointmentType.label || '');
            var locationId = encodeURIComponent(payload.selectedStoreId || '');
            var timeZoneId = encodeURIComponent(payload.timezoneId || '');

            var params = '?productId=' + productId + '&locationId=' + locationId + '&timezoneId=' + timeZoneId;
            url = url + params;

            return fetch(url, {
                method: 'GET',
                headers: {
                    Accept: 'application/json'
                },
            }).then(function (response) {
                if (!response.ok) {
                    throw new Error('Failed to load appointment dates');
                }
                return response.json();
            });
        },

        toDateOnly: function (dateStr) {
            var d = new Date(dateStr + 'T00:00:00');
            d.setHours(0, 0, 0, 0);
            return d;
        },

        formatDateToISO: function (dateObj) {
            var y = dateObj.getFullYear();
            var m = String(dateObj.getMonth() + 1).padStart(2, '0');
            var d = String(dateObj.getDate()).padStart(2, '0');
            return y + '-' + m + '-' + d;
        },

        normalizeAppointmentDatesResponse: function (responseData) {
            var raw = responseData || {};
            var bookingWindow = raw.bookingWindow || {};
            var startDate = this.toDateOnly(bookingWindow.startDate || this.formatDateToISO(new Date()));
            var endDate = this.toDateOnly(bookingWindow.endDate || this.formatDateToISO(startDate));
            var maxDaysAhead = typeof bookingWindow.maxDaysAhead === 'number' ? bookingWindow.maxDaysAhead : 180;

            var maxAllowedEndDate = new Date(startDate);
            maxAllowedEndDate.setDate(maxAllowedEndDate.getDate() + maxDaysAhead);

            var effectiveEndDate = endDate < maxAllowedEndDate ? endDate : maxAllowedEndDate;

            var availableDates = Array.isArray(raw.availableDates) ? raw.availableDates.slice() : [];
            var availableSet = new Set(availableDates);
            var allDates = [];
            var unavailableDates = [];

            var cursor = new Date(startDate);
            while (cursor <= effectiveEndDate) {
                var isoDate = this.formatDateToISO(cursor);
                allDates.push(isoDate);
                if (!availableSet.has(isoDate)) {
                    unavailableDates.push(isoDate);
                }
                cursor.setDate(cursor.getDate() + 1);
            }

            return {
                locationId: raw.locationId || '',
                productId: raw.productId || '',
                tenantUserId: raw.tenantUserId || '',
                timezoneId: raw.timezoneId || '',
                streamId: raw.streamId || '',
                minLeadTimeMinutes: raw.minLeadTimeMinutes || 0,
                bookingWindow: {
                    startDate: this.formatDateToISO(startDate),
                    endDate: this.formatDateToISO(endDate),
                    maxDaysAhead: maxDaysAhead,
                    effectiveEndDate: this.formatDateToISO(effectiveEndDate)
                },
                availableDates: availableDates,
                unavailableDates: unavailableDates,
                allDates: allDates
            };
        },

        loadAppointmentDates: function () {
            return this.fetchAppointmentDates()
                .then((responseData) => this.normalizeAppointmentDatesResponse(responseData));
        },

        fetchAppointmentSlots: function (selectedDate) {
            var url = document.querySelector('.booking-availability-slots') ? document.querySelector('.booking-availability-slots').value : '';

            var payload = this.getAppointmentDatesRequestPayload();
            var selectedType = payload.selectedAppointmentType || {};
            var params = new URLSearchParams({
                locationId: payload.selectedStoreId || '',
                timezoneId: payload.timezoneId || '',
                date: selectedDate || '',
                productId: selectedType.label || ''
            });

            return fetch(url + '?' + params.toString(), {
                method: 'GET',
                headers: {
                    Accept: 'application/json'
                }
            }).then(function (response) {
                if (!response.ok) {
                    throw new Error('Failed to load appointment slots');
                }
                return response.json();
            });
        },

        normalizeAppointmentSlotsResponse: function (responseData) {
            var raw = responseData || {};
            return {
                date: raw.date || '',
                slots: Array.isArray(raw.slots) ? raw.slots : []
            };
        },

        loadAppointmentSlots: function (selectedDate) {
            return this.fetchAppointmentSlots(selectedDate)
                .then((responseData) => this.normalizeAppointmentSlotsResponse(responseData));
        },

        /**
         * Restore state from cookie if exists
         */
        restoreState: function () {
            const savedSelection = CookieManager.get(this.COOKIE_NAME);
            if (savedSelection) {
                try {
                    this.selectedAppointmentType = savedSelection;

                    // Highlight the selected option
                    if (this.selectedAppointmentType === 1) {
                        this.appointmentOption1.classList.add('selected');
                        this.appointmentOption2.classList.remove('selected');
                    } else {
                        this.appointmentOption2.classList.add('selected');
                        this.appointmentOption1.classList.remove('selected');
                    }
                } catch (e) {
                    //console.warn('Failed to restore appointment selection:', e);
                    CookieManager.delete(this.COOKIE_NAME);
                }
            }
        },

        /**
         * Select an appointment option
         * @param {HTMLElement} optionElement - The clicked option element
         * @param {number} index - Index of the selected option
         */
        selectAppointmentOption: function (optionElement, index) {
            this.preventBackNavigation = false;

            // Store selection data

            this.selectedAppointmentType = {"id": index, "label": AppointmentBooking.getAppointmentTypeLabel(index)};

            // send tracking event for new appointment
            // this.trackEvent('newAppointment');

            // Save to cookie
            CookieManager.set(this.COOKIE_NAME, this.selectedAppointmentType);

            this.showNoteView();
        },

        /**
         * Show the note/confirmation view
         */
        showNoteView: function () {
            this.showView('note');

            // Update browser history
            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'note'
                }, 'Appointment Note', '');
            }
            this.scrollView('appointmentNoteView');

        },

        /**
         * Show the store selection view
         */
        showStoreSelection: function () {
            this.showView('storeSelection');
            this.updateStoreSelectionButtonState();
            // Update browser history
            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'storeSelection'
                }, 'Appointment Store Selection', '');
            }
        },

        /**
         * Show the date time view
         */
        showDateTime: function () {
            this.showView('dateTime');
            // Update browser history
            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'dateTime'
                }, 'Appointment Date Time', '');
            }
        },

        /**
         * Show the contact info view
         */
        showContactInfo: function () {
            this.populateContactStoreDetails();
            this.showView('contactInfo');
            // Update browser history
            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'contactInfo'
                }, 'Appointment Contact Info', '');
            }
        },

        /**
         * Show the confirmation view
         */
        showConfirmation: function () {
            this.showView('confirmation');
            // Update browser history
            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'confirmation'
                }, 'Appointment Confirmation', '');
            }
        },

        showRescheduleView: function () {
            this.showView('reschedule');

            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'reschedule'
                }, 'Reschedule Appointment', '');
            }
        },

        startRescheduleFlow: function () {
            var appointmentId =
                (this.confirmationData && (this.confirmationData.appointmentId || this.confirmationData.id)) ||
                (this.heldAppointmentData && (this.heldAppointmentData.appointmentId || this.heldAppointmentData.id)) ||
                '';

            if (!appointmentId) {
                this.showDateTimeError('Unable to get appointment details. Missing appointment id.');
                return;
            }

            this.showLoader();
            this.fetchAppointmentById(appointmentId)
                .then((appointmentData) => {
                    var context = {
                        locationId: appointmentData.locationId || (appointmentData.location && appointmentData.location.id) || '',
                        productId: appointmentData.productId || (appointmentData.product && appointmentData.product.id) || '',
                        timezoneId: appointmentData.timezone || appointmentData.timezoneId || ''
                    };

                    this.rescheduleAppointmentData = appointmentData;
                    this.currentRescheduleContext = context;

                    return this.loadAppointmentDatesByContext(context)
                        .then((appointmentDatesData) => {
                            if (typeof this.applyAppointmentDatesToRescheduleView === 'function') {
                                this.applyAppointmentDatesToRescheduleView(appointmentDatesData, appointmentData);
                            }

                            this.showRescheduleView();
                            this.scrollView('appointmentRescheduleView');
                        });
                })
                .catch((error) => {
                    this.showDateTimeError((error && error.message) || 'Unable to get appointment details.');
                })
                .finally(() => {
                    this.hideLoader();
                });
        },

        /**
         * Go back to selection view
         */
        goBack: function () {
            // Clear cookie and selection
            CookieManager.delete(this.COOKIE_NAME);
            this.selectedAppointmentType = null;

            // Show selection view
            this.showView('selection');
            this.scrollView('appointmentPurposeView');

            // Update browser history
            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'selection'
                }, 'Appointment Selection', '');
            }
        },
        /**
         * Go back to selection view
         */
        goBackToNote: function () {
            // Show selection view
            this.showView('note');
            this.scrollView('appointmentNoteView');

            // Update browser history
            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'note'
                }, 'Appointment Note', '');
            }
        },

        /**
         * Go back to store selection view
         */
        goBackToStoreSelection: function () {
            this.showView('storeSelection');
            this.scrollView('storeSelectionView');

            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'storeSelection'
                }, 'Appointment Store Selection', '');
            }
        },

        /**
         * Go back to date time view
         */
        goBackToDateTime: function () {
            this.showView('dateTime');
            this.scrollView('appointmentDateTimeView');

            if (window.history && window.history.pushState) {
                window.history.pushState({
                    view: 'dateTime'
                }, 'Appointment Date Time', '');
            }
        },

        /**
         * Show specific view and hide others
         * @param {string} viewName - Name of view to show
         */
        showView: function (viewName) {
            // Hide all views
            if (this.elements.selectionView) this.elements.selectionView.classList.add('hidden');
            if (this.elements.appointmentNoteView) this.elements.appointmentNoteView.classList.add('hidden');
            if (this.elements.storeSelectionView) this.elements.storeSelectionView.classList.add('hidden');
            if (this.elements.appointmentDateTimeView) this.elements.appointmentDateTimeView.classList.add('hidden');
            if (this.elements.appointmentContactInfoView) this.elements.appointmentContactInfoView.classList.add('hidden');
            if (this.elements.appointmentConfirmationView) this.elements.appointmentConfirmationView.classList.add('hidden');
            if (this.elements.appointmentRescheduleView) this.elements.appointmentRescheduleView.classList.add('hidden');

            // Show requested view
            switch (viewName) {
                case 'selection':
                    if (this.elements.selectionView) {
                        this.elements.selectionView.classList.remove('hidden');
                    }
                    break;
                case 'note':
                    if (this.elements.appointmentNoteView) {
                        this.elements.appointmentNoteView.classList.remove('hidden');
                        let data = this.selectedAppointmentType;
                        if (data.id == 1) {
                            this.elements.appointmentNoteTextWatches.classList.remove('hidden');
                            this.elements.appointmentNoteTextServicing.classList.add('hidden');
                        } else {
                            this.elements.appointmentNoteTextWatches.classList.add('hidden');
                            this.elements.appointmentNoteTextServicing.classList.remove('hidden');
                        }
                    }
                    break;
                case 'storeSelection':
                    if (this.elements.storeSelectionView) {
                        this.elements.storeSelectionView.classList.remove('hidden');
                    }
                    break;
                case 'dateTime':
                    if (this.elements.appointmentDateTimeView) {
                        this.elements.appointmentDateTimeView.classList.remove('hidden');
                    }
                    break;
                case 'contactInfo':
                    if (this.elements.appointmentContactInfoView) {
                        this.elements.appointmentContactInfoView.classList.remove('hidden');
                    }
                    break;
                case 'confirmation':
                    if (this.elements.appointmentConfirmationView) {
                        this.elements.appointmentConfirmationView.classList.remove('hidden');
                    }
                    break;
                case 'reschedule':
                    if (this.elements.appointmentRescheduleView) {
                        this.elements.appointmentRescheduleView.classList.remove('hidden');
                    }
                    break;
            }
        },

        /**
         * Scroll page
         * @param {string} elementId - ID of the element to scroll to
         */
        scrollView: function (elementId) {
            const offset = document.querySelector(".header.container").offsetHeight + 60;
            const el = document.getElementById(elementId);
            if (el) {
                const y = el.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({ top: y, behavior: 'smooth' });
            }
        },

        /**
         * Public method to check if user has made a selection
         * @return {boolean} True if user has selected an option
         */
        hasSelection: function () {
            return this.selectedAppointmentType !== null;
        },

        /**
         * Public method to get current selection
         * @return {Object|null} Selection data or null
         */
        getSelection: function () {
            return this.selectedAppointmentType;
        }
    };
    /**
     * Returns appointment type label based on number
     * @param {number} id - 1 or 2
     * @return {string} 'collection discovery' or 'watch servicing'
     */
    AppointmentBooking.getAppointmentTypeLabel = function(id) {
        if (id === 1) {
            return document.querySelector('.discoverVisitProximityId').value;
        } else if (id === 2) {
            return document.querySelector('.serviceVisitProximityId').value;
        }
        return '';
    };

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            AppointmentBooking.init();
        });
    } else {
        AppointmentBooking.init();
    }

    // Make AppointmentBooking available globally for debugging
    window.AppointmentBooking = AppointmentBooking;

    // Store reference to currentActiveLabel for backwards compatibility
    AppointmentBooking.currentActiveLabel = 'appointmentSelection';

})();

(function () {
    'use strict';
    // Shared helper functions for both contact info and reschedule views
    function normalizeTimeForDisplay(timeStr) {
        if (!timeStr) {
            return '';
        }
        return String(timeStr).slice(0, 5);
    }

    function toCalendarStamp(dateStr, timeStr) {
        if (!dateStr) {
            return '';
        }

        var timeValue = normalizeTimeForDisplay(timeStr) || '00:00';
        var parts = timeValue.split(':');
        var hh = parts[0] || '00';
        var mm = parts[1] || '00';
        var compactDate = String(dateStr).replace(/-/g, '');
        return compactDate + 'T' + hh + mm + '00Z';
    }

    function buildDisplayDateText(dateStr, timeStr) {
        if (!dateStr) {
            return '';
        }

        var dateObj = new Date(dateStr + 'T00:00:00');
        if (isNaN(dateObj.getTime())) {
            return dateStr;
        }

        var dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        var monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        var dateLabel = dayNames[dateObj.getDay()] + ' ' + String(dateObj.getDate()).padStart(2, '0') + ' ' + monthNames[dateObj.getMonth()] + ' ' + dateObj.getFullYear();
        var timeLabel = normalizeTimeForDisplay(timeStr);

        return timeLabel ? (dateLabel + ' at ' + timeLabel) : dateLabel;
    }

    function updateAppointmentInformationScript(appointmentName, appointmentDate, appointmentTime) {
        var existingScriptTag = document.getElementById('appointmentInformationScript');
        if (existingScriptTag && existingScriptTag.parentNode) {
            existingScriptTag.parentNode.removeChild(existingScriptTag);
        }

        appointmentTime = normalizeTimeForDisplay(appointmentTime);

        var dayOfWeek = '';
        if (appointmentDate) {
            var parts = appointmentDate.split('-');
            var year = parseInt(parts[0], 10);
            var month = parseInt(parts[1], 10) - 1;
            var day = parseInt(parts[2], 10);
            var dateObj = new Date(year, month, day);
            var days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            dayOfWeek = days[dateObj.getDay()];
        }
        appointmentName = appointmentName === 'service-visit' ? 'watch servicing' : 'collection discovery';
        var scriptTag = document.createElement('script');
        scriptTag.type = 'text/javascript';
        scriptTag.id = 'appointmentInformationScript';
        scriptTag.text = "window.appointmentInformation = '" + (appointmentName || '') + "|" + dayOfWeek + "|" + (appointmentDate || '') + "|" + (appointmentTime || '') + "';";
        document.head.appendChild(scriptTag);
    }

    function initContactInfoView() {
        var root = document.getElementById('appointmentContactInfoView');
        if (!root) return;

        var bookingApi = window.AppointmentBooking;
        if (!bookingApi) return;

        var form = root.querySelector('#appointmentContactForm');
        var submitBtn = root.querySelector('#contactInfoSubmitButton');
        var mandatoryCheckbox = root.querySelector('#contactTermsCheckbox');
        var mandatoryCheckboxError = root.querySelector('#contactTermsError');
        var marketingCheckbox = root.querySelector('#contactMarketingCheckbox');
        var marketingCheckboxError = root.querySelector('#contactMarketingError');
        var phoneNumberInput = root.querySelector('#contactPhoneNumber');
        var mandatoryCheckboxWrap = mandatoryCheckbox ? mandatoryCheckbox.closest('.contact-checkbox-wrap') : null;
        var marketingCheckboxWrap = marketingCheckbox ? marketingCheckbox.closest('.contact-checkbox-wrap') : null;
        var submitErrorNode = null;

        if (!form || !submitBtn || !mandatoryCheckbox || !marketingCheckbox) return;

        var requiredFields = form.querySelectorAll('.contact-input-group input[required], .contact-input-group select[required]');

        function ensureSubmitErrorNode() {
            if (submitErrorNode) {
                return submitErrorNode;
            }

            submitErrorNode = document.createElement('div');
            submitErrorNode.className = 'contact-error-message';
            submitErrorNode.style.display = 'none';
            submitErrorNode.style.marginTop = '12px';
            submitErrorNode.style.textAlign = 'center';

            var submitWrap = form.querySelector('.contact-submit-wrap') || form;
            submitWrap.parentNode.insertBefore(submitErrorNode, submitWrap.nextSibling);
            return submitErrorNode;
        }

        function showSubmitError(message) {
            var node = ensureSubmitErrorNode();
            node.textContent = message || 'Unable to confirm appointment. Please try again.';
            node.style.display = 'block';
        }

        function clearSubmitError() {
            var node = ensureSubmitErrorNode();
            node.textContent = '';
            node.style.display = 'none';
        }

        function normalizeTimeForDisplay(timeStr) {
            if (!timeStr) {
                return '';
            }
            return String(timeStr).slice(0, 5);
        }

        function buildLocationText(storeData) {
            if (!storeData) {
                return '';
            }

            return [storeData.address1 || '', storeData.city || '', storeData.country || '']
                .filter(function (value) { return !!value; })
                .join(', ');
        }

        function toCalendarStamp(dateStr, timeStr) {
            if (!dateStr) {
                return '';
            }

            var timeValue = normalizeTimeForDisplay(timeStr) || '00:00';
            var parts = timeValue.split(':');
            var hh = parts[0] || '00';
            var mm = parts[1] || '00';
            var compactDate = String(dateStr).replace(/-/g, '');
            return compactDate + 'T' + hh + mm + '00Z';
        }

        function computeEndTime(startTime, fallbackEndTime) {
            if (fallbackEndTime) {
                return fallbackEndTime;
            }

            var start = normalizeTimeForDisplay(startTime);
            if (!start) {
                return '';
            }

            var parts = start.split(':');
            var hours = parseInt(parts[0], 10);
            var minutes = parseInt(parts[1], 10);
            if (isNaN(hours) || isNaN(minutes)) {
                return '';
            }

            minutes += 30;
            if (minutes >= 60) {
                hours += 1;
                minutes -= 60;
            }

            return String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0');
        }

        function buildDisplayDateText(dateStr, timeStr) {
            if (!dateStr) {
                return '';
            }

            var dateObj = new Date(dateStr + 'T00:00:00');
            if (isNaN(dateObj.getTime())) {
                return dateStr;
            }

            var dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            var monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            var dateLabel = dayNames[dateObj.getDay()] + ' ' + String(dateObj.getDate()).padStart(2, '0') + ' ' + monthNames[dateObj.getMonth()] + ' ' + dateObj.getFullYear();
            var timeLabel = normalizeTimeForDisplay(timeStr);

            return timeLabel ? (dateLabel + ' at ' + timeLabel) : dateLabel;
        }

        function getContactFormData() {
            return {
                appointmentId: (bookingApi.heldAppointmentData && bookingApi.heldAppointmentData.appointmentId) || '',
                salutation: (form.querySelector('#contactTitle') || {}).value || '',
                firstName: (form.querySelector('#contactFirstName') || {}).value || '',
                lastName: (form.querySelector('#contactLastName') || {}).value || '',
                email: (form.querySelector('#contactEmail') || {}).value || '',
                phone: (form.querySelector('#contactPhoneNumber') || {}).value || '',
                customProperties: {
                    mobileCountryCode: (form.querySelector('#contactPhoneCode') || {}).value || '',
                    countryOfResidence: (form.querySelector('#contactCountry') || {}).value || '',
                    privacyPolicy: !!(form.querySelector('#contactTermsCheckbox') || {}).checked,
                    marketingOptIn: !!(form.querySelector('#contactMarketingCheckbox') || {}).checked,
                    communicationPreferences: document.querySelector('input[name="contactPreferredCommunication"]:checked').value,
                    additionalComments: (form.querySelector('#contactComments') || {}).value || '',
                    notifyEarlierSlot: !!(form.querySelector('#contactNotifyEarlierCheckbox') || {}).checked
                }
            };
        }

        function buildConfirmationData(responseData, payload) {
            var response = responseData || {};
            var storeData = bookingApi.getSelectedStoreData ? bookingApi.getSelectedStoreData() : null;
            var selectedType = bookingApi.getSelectedAppointmentTypePayload();
            var dateStr = response.date || payload.appointmentDate || '';
            var startTime = response.startTime || payload.startTime || '';
            var endTime = response.endTime || payload.endTime || computeEndTime(startTime, '');

            var purpose = (response.purpose || response.selectedAppointmentType || selectedType.label || '') == document.querySelector('.discoverVisitProximityId').value ? 'Collection Discovery' : 'Watch Servicing';
            var location = response.location || buildLocationText(storeData);
            var displayDateText = response.displayDateText || buildDisplayDateText(dateStr, startTime);

            return {
                appointmentId: response.appointmentId || payload.appointmentId || '',
                retailerName: response.locationName || (storeData && (storeData.title || storeData.name)) || '',
                purpose: purpose,
                displayDateText: displayDateText,
                location: location,
                startTimeISO: response.startTimeISO || toCalendarStamp(dateStr, startTime),
                endTimeISO: response.endTimeISO || toCalendarStamp(dateStr, endTime)
            };
        }

        function validateMandatoryCheckbox(showError) {
            var isChecked = mandatoryCheckbox.checked;

            if (mandatoryCheckboxWrap) {
                if (isChecked) {
                    mandatoryCheckboxWrap.classList.remove('invalid-field');
                } else {
                    mandatoryCheckboxWrap.classList.add('invalid-field');
                }
            }

            if (mandatoryCheckboxError) {
                mandatoryCheckboxError.style.display = (!isChecked && showError) ? 'block' : 'none';
            }

            return isChecked;
        }

        function validateMarketingCheckbox(showError) {
            var isChecked = marketingCheckbox.checked;

            if (marketingCheckboxWrap) {
                if (isChecked) {
                    marketingCheckboxWrap.classList.remove('invalid-field');
                } else {
                    marketingCheckboxWrap.classList.add('invalid-field');
                }
            }

            if (marketingCheckboxError) {
                marketingCheckboxError.style.display = (!isChecked && showError) ? 'block' : 'none';
            }

            return isChecked;
        }

        function validateEmail(emailStr) {
            var pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return pattern.test(emailStr);
        }

        function validateField(inputElement) {
            var inputGroup = inputElement.closest('.contact-input-group');
            if (!inputGroup) return true;

            var isValid = true;
            var value = inputElement.value.trim();

            if (inputElement.type === 'email') {
                isValid = validateEmail(value);
            } else if (inputElement.id === 'contactPhoneNumber') {
                isValid = value !== '' && value.length >= 10;
            } else {
                isValid = value !== '';
            }

            if (isValid) inputGroup.classList.remove('invalid-field');
            else inputGroup.classList.add('invalid-field');

            return isValid;
        }

        function validateFormState() {
            var allValid = true;

            for (var i = 0; i < requiredFields.length; i++) {
                var input = requiredFields[i];
                var value = input.value.trim();

                if (input.type === 'email') {
                    if (!validateEmail(value)) allValid = false;
                } else if (value === '') {
                    allValid = false;
                }
            }

            if (!validateMandatoryCheckbox(false)) allValid = false;
            if (!validateMarketingCheckbox(false)) allValid = false;

            // if (allValid) submitBtn.removeAttribute('disabled');
            // else submitBtn.setAttribute('disabled', 'true');
            return allValid;
        }

        phoneNumberInput.addEventListener('input', function (e) {
            e.target.value = e.target.value.replace(/\D/g, '');
            validateFormState();
        });

        form.addEventListener('input', function (e) {
            if (e.target !== phoneNumberInput) validateFormState();
        });

        form.addEventListener('change', function () {
            validateFormState();
        });

        form.addEventListener('submit', function (e) {
            e.preventDefault();

            var isFormValid = true;
            for (var i = 0; i < requiredFields.length; i++) {
                if (!validateField(requiredFields[i])) isFormValid = false;
            }
            // if user entered phone number value then validate it
            var phoneNumberInput = document.getElementById('contactPhoneNumber')
            var phoneNumberValue = phoneNumberInput ? phoneNumberInput.value.trim() : '';
            if (phoneNumberValue != '' && !validateField(phoneNumberInput)) isFormValid = false;

            if (!validateMandatoryCheckbox(true)) {
                isFormValid = false;
            }

            if (!validateMarketingCheckbox(true)) {
                isFormValid = false;
            }

            if (isFormValid && mandatoryCheckbox.checked && marketingCheckbox.checked) {
                clearSubmitError();

                var contactData = getContactFormData();

                if (!contactData.appointmentId) {
                    bookingApi.showDateTimeError('Unable to confirm appointment. Missing appointment hold id.');
                    return;
                }

                bookingApi.showLoader();
                bookingApi.fetchAppointmentConfirm(contactData)
                    .then(function (responseData) {
                        var confirmationData = buildConfirmationData(responseData, contactData);
                        bookingApi.confirmationData = confirmationData;

                        if (typeof bookingApi.renderConfirmationData === 'function') {
                            bookingApi.renderConfirmationData(confirmationData);
                        }

                        bookingApi.trackEvent('bookAppointment');

                        bookingApi.showConfirmation();
                        bookingApi.scrollView('appointmentConfirmationView');
                    })
                    .catch(function (error) {
                        bookingApi.showDateTimeError((error && error.message) || 'Unable to confirm appointment. Please try again.');
                    })
                    .finally(function () {
                        bookingApi.hideLoader();
                    });
            }
        });

        mandatoryCheckbox.addEventListener('change', function () {
            validateMandatoryCheckbox(false);
        });

        marketingCheckbox.addEventListener('change', function () {
            validateMarketingCheckbox(false);
        });
    }

    function initConfirmationView() {
        var root = document.getElementById('appointmentConfirmationView');
        if (!root) return;

        var bookingApi = window.AppointmentBooking;

        var defaultData = {
            retailerName: '',
            purpose: '',
            displayDateText: '',
            location: '',
            startTimeISO: '',
            endTimeISO: ''
        };

        var retailerNode = root.querySelector('#confirmationRetailerName');
        var purposeNode = root.querySelector('#confirmationPurpose');
        var dateNode = root.querySelector('#confirmationDate');
        var locationNode = root.querySelector('#confirmationLocation');
        var calendarLinkNode = root.querySelector('#confirmationAddToCalendar');
        var homeButton = root.querySelector('#confirmationHomeButton');
        var modifyButton = root.querySelector('#confirmationModifyButton');

        function renderConfirmationData(data) {
            var viewData = data || defaultData;

            var retailerText = document.getElementById('confirmationRetailerValue').value.replace('{Retailer Name}', '<br>' + (viewData.retailerName || ''));
            
            if (retailerNode) retailerNode.innerHTML = retailerText || '';
            if (purposeNode) purposeNode.textContent = viewData.purpose || '';
            if (dateNode) dateNode.textContent = viewData.displayDateText || '';
            if (locationNode) locationNode.textContent = viewData.location || '';

            if (calendarLinkNode) {
                var baseUrl = 'https://www.google.com/calendar/render?action=TEMPLATE';
                var textParam = '&text=' + encodeURIComponent(viewData.purpose || 'Rolex appointment');
                var datesParam = '';
                if (viewData.startTimeISO && viewData.endTimeISO) {
                    datesParam = '&dates=' + viewData.startTimeISO + '/' + viewData.endTimeISO;
                }
                var detailsParam = '&details=' + encodeURIComponent('Appointment confirmation details');
                var locationParam = '&location=' + encodeURIComponent(viewData.location || '');
                calendarLinkNode.setAttribute('href', baseUrl + textParam + datesParam + detailsParam + locationParam);
            }
        }

        if (bookingApi) {
            bookingApi.renderConfirmationData = renderConfirmationData;
            renderConfirmationData(bookingApi.confirmationData || defaultData);
        } else {
            renderConfirmationData(defaultData);
        }

        if (homeButton) {
            homeButton.addEventListener('click', function () {
                window.location.href = '/';
            });
        }

        if (modifyButton) {
            modifyButton.addEventListener('click', function () {
                if (bookingApi && typeof bookingApi.startRescheduleFlow === 'function') {
                    bookingApi.startRescheduleFlow();
                }
            });
        }
    }

    function initDateTimeView() {
        var bookingApi = window.AppointmentBooking;
        var dateRangeDisplay = document.getElementById('dateRangeDisplay');
        var dateGrid = document.getElementById('dateGrid');
        var timeSlotsGrid = document.getElementById('timeSlotsGrid');
        var nextSubmitBtn = document.getElementById('dateTimeNextButton');
        var prevBtn = document.getElementById('prevBtn');
        var nextBtn = document.getElementById('nextBtn');

        if (!bookingApi || !dateRangeDisplay || !dateGrid || !timeSlotsGrid || !nextSubmitBtn || !prevBtn || !nextBtn) {
            return;
        }

        var MAX_FUTURE_DAYS = 60;
        var WINDOW_SIZE = 6;
        var monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        var dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        var currentStartDate = new Date();
        currentStartDate.setHours(0, 0, 0, 0);
        var selectedDateStr = null;
        var selectedTimeStr = null;

        var availableDatesSet = new Set();
        var bookingWindowStartDate = new Date(currentStartDate);
        var bookingWindowEndDate = new Date(currentStartDate);
        bookingWindowEndDate.setDate(bookingWindowEndDate.getDate() + MAX_FUTURE_DAYS);
        var slotsByDate = {};
        var cancelConfirmationModal = null;
        var dateTimeErrorModal = null;

        function ensureDateTimeErrorModal() {
            if (dateTimeErrorModal) {
                return dateTimeErrorModal;
            }

            var root = document.getElementById('appointmentBookingContainer') || document.body;

            var overlay = document.createElement('div');
            overlay.id = 'dateTimeHoldErrorModal';
            overlay.style.display = 'none';
            overlay.style.position = 'fixed';
            overlay.style.inset = '0';
            overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.45)';
            overlay.style.zIndex = '9999';
            overlay.style.alignItems = 'center';
            overlay.style.justifyContent = 'center';

            var dialog = document.createElement('div');
            dialog.style.backgroundColor = '#ffffff';
            dialog.style.width = '90%';
            dialog.style.maxWidth = '420px';
            dialog.style.padding = '24px 20px';
            dialog.style.borderRadius = '4px';
            dialog.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.18)';
            dialog.style.textAlign = 'center';

            var message = document.createElement('div');
            message.className = 'rolex-v7-fixed16';
            message.id = 'dateTimeHoldErrorModalMessage';
            message.style.color = '#352520';
            message.style.marginBottom = '18px';
            message.style.whiteSpace = 'normal';
            message.style.wordBreak = 'break-word';
            message.style.overflowWrap = 'anywhere';
            message.style.maxHeight = '180px';
            message.style.overflowY = 'auto';
            message.style.paddingRight = '2px';

            var okButton = document.createElement('button');
            okButton.type = 'button';
            okButton.className = 'btn btn-primary rolex-button rolex-button-green';
            okButton.textContent = 'OK';
            okButton.addEventListener('click', function () {
                overlay.style.display = 'none';
            });

            var actions = document.createElement('div');
            actions.style.display = 'flex';
            actions.style.justifyContent = 'flex-end';
            actions.appendChild(okButton);

            dialog.appendChild(message);
            dialog.appendChild(actions);
            overlay.appendChild(dialog);
            root.appendChild(overlay);

            dateTimeErrorModal = {
                overlay: overlay,
                message: message
            };

            return dateTimeErrorModal;
        }

        bookingApi.showDateTimeError = function (message) {
            var modal = ensureDateTimeErrorModal();
            if (!modal) {
                return;
            }

            modal.message.textContent = message || 'Unable to hold appointment slot. Please try again.';
            modal.overlay.style.display = 'flex';
        };

        bookingApi.clearDateTimeError = function () {
            var modal = ensureDateTimeErrorModal();
            if (!modal) {
                return;
            }

            modal.message.textContent = '';
            modal.overlay.style.display = 'none';
        };

        bookingApi.resetDateTimeSelection = function () {
            selectedDateStr = null;
            selectedTimeStr = null;
            currentStartDate = new Date(bookingWindowStartDate);
            slotsByDate = {};
            bookingApi.selectedDateTime = null;
            bookingApi.clearDateTimeError();
            renderDateWindow(currentStartDate);
            validateFormState();
        };

        bookingApi.applyAppointmentDatesToDateTimeView = function (appointmentDatesData) {
            var startDateStr = appointmentDatesData && appointmentDatesData.bookingWindow ? appointmentDatesData.bookingWindow.startDate : null;
            var endDateStr = appointmentDatesData && appointmentDatesData.bookingWindow ? appointmentDatesData.bookingWindow.effectiveEndDate : null;

            if (startDateStr) {
                bookingWindowStartDate = new Date(startDateStr + 'T00:00:00');
            }
            if (endDateStr) {
                bookingWindowEndDate = new Date(endDateStr + 'T00:00:00');
            }

            availableDatesSet = new Set((appointmentDatesData && appointmentDatesData.availableDates) || []);

            selectedDateStr = null;
            selectedTimeStr = null;
            slotsByDate = {};
            bookingApi.selectedDateTime = null;
            bookingApi.clearDateTimeError();
            currentStartDate = new Date(bookingWindowStartDate);
            renderDateWindow(currentStartDate);
            validateFormState();
        };

        function setupEventListeners() {
            prevBtn.addEventListener('click', function () { changeDateWindow(-WINDOW_SIZE); });
            nextBtn.addEventListener('click', function () { changeDateWindow(WINDOW_SIZE); });
            nextSubmitBtn.addEventListener('click', function () {
                if (selectedDateStr && selectedTimeStr) {
                    bookingApi.clearDateTimeError();
                    bookingApi.showLoader();
                    bookingApi.holdSelectedAppointment()
                        .then(function (responseData) {
                            var selectedType = bookingApi.getSelectedAppointmentTypePayload();
                            updateAppointmentInformationScript(
                                responseData.productName || responseData.selectedAppointmentType || selectedType.label || '',
                                responseData.date || selectedDateStr || '',
                                responseData.startTime || selectedTimeStr || ''
                            );
                            bookingApi.showContactInfo();
                            bookingApi.scrollView('appointmentContactInfoView');
                        })
                        .catch(function (error) {
                            bookingApi.showDateTimeError((error && error.message) || 'Unable to hold selected slot. Please try again.');
                        })
                        .finally(function () {
                            bookingApi.hideLoader();
                        });
                }
            });
        }

        function changeDateWindow(daysOffset) {
            selectedTimeStr = null;
            selectedDateStr = null;
            timeSlotsGrid.innerHTML = '';
            document.querySelectorAll('.date-tile').forEach(function (el) { el.classList.remove('selected'); });

            var targetStartDate = new Date(currentStartDate);
            targetStartDate.setDate(targetStartDate.getDate() + daysOffset);

            if (targetStartDate < bookingWindowStartDate && daysOffset < 0) {
                currentStartDate = new Date(bookingWindowStartDate);
            } else if (targetStartDate > bookingWindowEndDate && daysOffset > 0) {
                return;
            } else {
                currentStartDate = targetStartDate;
            }

            renderDateWindow(currentStartDate);
        }

        function formatDateString(dateObj) {
            var year = dateObj.getFullYear();
            var month = String(dateObj.getMonth() + 1).padStart(2, '0');
            var day = String(dateObj.getDate()).padStart(2, '0');
            return year + '-' + month + '-' + day;
        }

        function renderDateWindow(startDate) {
            dateGrid.innerHTML = '';

            var windowDates = [];
            var workingDate = new Date(startDate);

            for (var i = 0; i < WINDOW_SIZE; i++) {
                windowDates.push(new Date(workingDate));
                workingDate.setDate(workingDate.getDate() + 1);
            }

            if (startDate <= bookingWindowStartDate) {
                prevBtn.setAttribute('disabled', 'true');
                prevBtn.style.opacity = '0.3';
                prevBtn.style.cursor = 'not-allowed';
            } else {
                prevBtn.removeAttribute('disabled');
                prevBtn.style.opacity = '1';
                prevBtn.style.cursor = 'pointer';
            }

            var nextWindowCheck = new Date(startDate);
            nextWindowCheck.setDate(nextWindowCheck.getDate() + WINDOW_SIZE);

            if (nextWindowCheck > bookingWindowEndDate) {
                nextBtn.setAttribute('disabled', 'true');
                nextBtn.style.opacity = '0.3';
                nextBtn.style.cursor = 'not-allowed';
            } else {
                nextBtn.removeAttribute('disabled');
                nextBtn.style.opacity = '1';
                nextBtn.style.cursor = 'pointer';
            }

            var firstDate = windowDates[0];
            var lastDate = windowDates[WINDOW_SIZE - 1];

            if (firstDate.getMonth() === lastDate.getMonth()) {
                dateRangeDisplay.textContent = firstDate.getDate() + '-' + lastDate.getDate() + ' ' + monthNames[firstDate.getMonth()] + ' ' + firstDate.getFullYear();
            } else {
                dateRangeDisplay.textContent = firstDate.getDate() + ' ' + monthNames[firstDate.getMonth()] + ' - ' + lastDate.getDate() + ' ' + monthNames[lastDate.getMonth()] + ' ' + firstDate.getFullYear();
            }

            windowDates.forEach(function (date) {
                var dateStr = formatDateString(date);
                var tile = document.createElement('div');
                tile.classList.add('date-tile', 'rolex-v7-fixed16', 'rolex-push-v7--very-small-title', 'pb-2');

                if (date < bookingWindowStartDate || date > bookingWindowEndDate || !availableDatesSet.has(dateStr)) {
                    tile.classList.add('disabled');
                }

                if (selectedDateStr === dateStr) {
                    tile.classList.add('selected');
                }

                tile.innerHTML = '<span class="day-num">' + date.getDate() + '</span>' +
                    '<span class="day-name">' + dayNames[date.getDay()] + '</span>';

                tile.addEventListener('click', function () {
                    if (!tile.classList.contains('disabled')) {
                        document.querySelectorAll('.date-tile').forEach(function (el) { el.classList.remove('selected'); });
                        tile.classList.add('selected');
                        selectedDateStr = dateStr;
                        selectedTimeStr = null;
                        loadTimeSlotsForDate(dateStr);
                        validateFormState();
                    }
                });

                dateGrid.appendChild(tile);
            });

            renderTimeSlots(selectedDateStr);
            validateFormState();
        }

        function formatSlotLabel(timeStr) {
            return (timeStr || '').slice(0, 5);
        }

        function loadTimeSlotsForDate(dateStr) {
            selectedTimeStr = null;
            slotsByDate[dateStr] = [];
            bookingApi.selectedDateTime = null;
            bookingApi.clearDateTimeError();
            renderTimeSlots(dateStr);
            validateFormState();

            bookingApi.showLoader();
            bookingApi.loadAppointmentSlots(dateStr)
                .then(function (slotResponse) {
                    slotsByDate[dateStr] = (slotResponse && Array.isArray(slotResponse.slots)) ? slotResponse.slots : [];
                    renderTimeSlots(dateStr);
                    validateFormState();
                })
                .catch(function (error) {
                    bookingApi.showDateTimeError((error && error.message) || 'Unable to load time slots. Please try again.');
                })
                .finally(function () {
                    bookingApi.hideLoader();
                });
        }

        function renderTimeSlots(dateStr) {
            timeSlotsGrid.innerHTML = '';

            if (!dateStr) {
                var messageNode = document.getElementById('selectTimeMessageText');
                var value = messageNode ? messageNode.value : '';
                timeSlotsGrid.innerHTML = '<p style="grid-column: span 3; font-size: 13px; color: #8C8280;" class="rolex-v7-fixed16 rolex-push-v7--very-small-title pb-4">' + value + '</p>';
                return;
            }

            var slots = slotsByDate[dateStr] || [];

            if (!slots.length) {
                timeSlotsGrid.innerHTML = '<p style="grid-column: span 3; font-size: 13px; color: #8C8280;" class="rolex-v7-fixed16 rolex-push-v7--very-small-title pb-4">No slots available for selected date.</p>';
                return;
            }

            slots.forEach(function (slot) {
                var timeTile = document.createElement('div');
                timeTile.classList.add('time-tile', 'rolex-v7-fixed16', 'rolex-push-v7--very-small-title');
                timeTile.textContent = formatSlotLabel(slot.startTime);

                if (selectedTimeStr === slot.startTime) {
                    timeTile.classList.add('selected');
                }

                timeTile.addEventListener('click', function () {
                    document.querySelectorAll('.time-tile').forEach(function (el) { el.classList.remove('selected'); });
                    timeTile.classList.add('selected');
                    selectedTimeStr = slot.startTime;
                    bookingApi.selectedDateTime = {
                        date: selectedDateStr,
                        startTime: slot.startTime,
                        endTime: slot.endTime || ''
                    };
                    bookingApi.clearDateTimeError();
                    validateFormState();
                });

                timeSlotsGrid.appendChild(timeTile);
            });
        }

        function validateFormState() {
            if (selectedDateStr && selectedTimeStr) {
                nextSubmitBtn.removeAttribute('disabled');
            } else {
                nextSubmitBtn.setAttribute('disabled', 'true');
            }
        }

        renderDateWindow(currentStartDate);
        setupEventListeners();
    }

    function initRescheduleView() {
        var bookingApi = window.AppointmentBooking;
        var dateRangeDisplay = document.getElementById('rescheduleDateRangeDisplay');
        var dateGrid = document.getElementById('rescheduleDateGrid');
        var timeSlotsGrid = document.getElementById('rescheduleTimeSlotsGrid');
        var prevBtn = document.getElementById('reschedulePrevBtn');
        var nextBtn = document.getElementById('rescheduleNextBtn');
        var cancelButton = document.getElementById('cancelAppointmentButton');
        var confirmButton = document.getElementById('confirmRescheduleButton');
        var locationNode = document.getElementById('rescheduleLocationName');
        var rescheduleMainHeader = document.getElementById('rescheduleMainHeader');
        var rescheduleContentView = document.getElementById('rescheduleView');
        var cancelSuccessView = document.getElementById('cancelAppointmentSuccessView');
        var cancelSuccessRetailerNode = document.getElementById('cancelledAppointmentRetailerName');
        var bookNewAppointmentButton = document.getElementById('bookNewAppointmentButton');

        if (!bookingApi || !dateRangeDisplay || !dateGrid || !timeSlotsGrid || !prevBtn || !nextBtn || !confirmButton) {
            return;
        }

        var MAX_FUTURE_DAYS = 60;
        var WINDOW_SIZE = 6;
        var monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        var dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        var currentStartDate = new Date();
        var selectedDateStr = null;
        var selectedTimeStr = null;
        var availableDatesSet = new Set();
        var bookingWindowStartDate = new Date(currentStartDate);
        var bookingWindowEndDate = new Date(currentStartDate);
        bookingWindowEndDate.setDate(bookingWindowEndDate.getDate() + MAX_FUTURE_DAYS);
        var slotsByDate = {};
        var cancelConfirmationModal = null;

        bookingApi.resetRescheduleSelection = function () {
            selectedDateStr = null;
            selectedTimeStr = null;
            currentStartDate = new Date(bookingWindowStartDate);
            slotsByDate = {};
            bookingApi.selectedRescheduleDateTime = null;
            showRescheduleContentView();
            renderDateWindow(currentStartDate);
            validateFormState();
        };

        bookingApi.applyAppointmentDatesToRescheduleView = function (appointmentDatesData, appointmentData) {
            var startDateStr = appointmentDatesData && appointmentDatesData.bookingWindow ? appointmentDatesData.bookingWindow.startDate : null;
            var endDateStr = appointmentDatesData && appointmentDatesData.bookingWindow ? appointmentDatesData.bookingWindow.effectiveEndDate : null;

            if (startDateStr) {
                bookingWindowStartDate = new Date(startDateStr + 'T00:00:00');
            }
            if (endDateStr) {
                bookingWindowEndDate = new Date(endDateStr + 'T00:00:00');
            }

            availableDatesSet = new Set((appointmentDatesData && appointmentDatesData.availableDates) || []);

            selectedDateStr = null;
            selectedTimeStr = null;
            slotsByDate = {};
            bookingApi.selectedRescheduleDateTime = null;
            currentStartDate = new Date(bookingWindowStartDate);
            showRescheduleContentView();

            if (locationNode) {
                locationNode.textContent = (appointmentData && (appointmentData.locationName || (appointmentData.location && appointmentData.location.name))) || '';
            }

            renderDateWindow(currentStartDate);
            validateFormState();
        };

        bookingApi.showCancelAppointmentModal = function () {
            showCancelConfirmationModal();
        };

        function ensureCancelConfirmationModal() {
            if (cancelConfirmationModal) {
                return cancelConfirmationModal;
            }

            var root = document.getElementById('appointmentBookingContainer') || document.body;
            var overlay = document.createElement('div');
            overlay.className = 'appointment-cancel-modal hidden';

            var dialog = document.createElement('div');
            dialog.className = 'appointment-cancel-modal__dialog';

            var title = document.createElement('div');
            title.className = 'appointment-cancel-modal__title rolex-v7-headline36 rolex-push-v7--large-title';
            title.textContent = 'Cancel Appointment';

            var message = document.createElement('div');
            message.className = 'appointment-cancel-modal__message rolex-v7-body20-light';
            message.textContent = 'Are you sure you want to cancel your Rolex Appointment?';

            var actions = document.createElement('div');
            actions.className = 'appointment-cancel-modal__actions';

            var nevermindButton = document.createElement('button');
            nevermindButton.type = 'button';
            nevermindButton.className = 'btn btn-primary rolex-button appointment-cancel-modal__button appointment-cancel-modal__button--secondary';
            nevermindButton.style.justifyContent = 'center';
            nevermindButton.style.display = 'flex';
            nevermindButton.textContent = 'Nevermind';

            var confirmCancelButton = document.createElement('button');
            confirmCancelButton.type = 'button';
            confirmCancelButton.className = 'btn btn-primary rolex-button rolex-button-green appointment-cancel-modal__button';
            confirmCancelButton.style.justifyContent = 'center';
            confirmCancelButton.textContent = 'Confirm';

            nevermindButton.addEventListener('click', hideCancelConfirmationModal);
            confirmCancelButton.addEventListener('click', function () {
                hideCancelConfirmationModal();
                performCancelAppointment();
            });

            actions.appendChild(nevermindButton);
            actions.appendChild(confirmCancelButton);
            dialog.appendChild(title);
            dialog.appendChild(message);
            dialog.appendChild(actions);
            overlay.appendChild(dialog);
            root.appendChild(overlay);

            cancelConfirmationModal = {
                overlay: overlay,
                confirmButton: confirmCancelButton,
                cancelButton: nevermindButton
            };

            return cancelConfirmationModal;
        }

        function hideCancelConfirmationModal() {
            var modal = ensureCancelConfirmationModal();
            modal.overlay.classList.add('hidden');
        }

        function showCancelConfirmationModal() {
            var modal = ensureCancelConfirmationModal();
            modal.overlay.classList.remove('hidden');
        }

        function resetBookingFlowToStart() {
            bookingApi.resetAllBookingState();
            bookingApi.removeAppointmentInformationScript();
            bookingApi.preventBackNavigation = true;
            bookingApi.showView('selection');
            bookingApi.scrollView('appointmentPurposeView');

            if (window.history && window.history.replaceState) {
                window.history.replaceState({
                    view: 'selection'
                }, 'Appointment Selection', '');
            }
        }

        function showCancelSuccessView() {
            
            bookingApi.preventBackNavigation = true;
            document.getElementById('appointmentConfirmationView').classList.add('hidden');
            document.getElementById('appointmentRescheduleView').classList.remove('hidden');

            if (rescheduleMainHeader) {
                rescheduleMainHeader.classList.add('hidden');
            }

            if (rescheduleContentView) {
                rescheduleContentView.classList.add('hidden');
            }

            if (cancelSuccessRetailerNode) {
                cancelSuccessRetailerNode.textContent = (bookingApi.rescheduleAppointmentData && (bookingApi.rescheduleAppointmentData.locationName || (bookingApi.rescheduleAppointmentData.location && bookingApi.rescheduleAppointmentData.location.name))) || (locationNode ? locationNode.textContent : '') || 'BBJ';
            }

            if (cancelSuccessView) {
                cancelSuccessView.classList.remove('hidden');
            }
        }

        function performCancelAppointment() {
            var cancelPayload = bookingApi.buildCancelPayload();

            if (!cancelPayload.appointmentId) {
                bookingApi.showDateTimeError('Unable to cancel appointment. Missing appointment id.');
                return;
            }

            bookingApi.showLoader();
            bookingApi.fetchAppointmentCancel(cancelPayload)
                .then(function () {
                    bookingApi.removeAppointmentInformationScript();
                    showCancelSuccessView();
                    bookingApi.scrollView('appointmentRescheduleView');
                })
                .catch(function (error) {
                    var errorMessage = 'Failed to cancel appointment. Please try again.';
                    if (error && error.message) {
                        try {
                            const messageObj = JSON.parse(error.message);
                            errorMessage = messageObj.error?.message || errorMessage;
                        } catch (e) {
                        }
                    }
                    bookingApi.showDateTimeError(errorMessage);
                })
                .finally(function () {
                    bookingApi.hideLoader();
                });
        }

        function setupEventListeners() {
            prevBtn.addEventListener('click', function () { changeDateWindow(-WINDOW_SIZE); });
            nextBtn.addEventListener('click', function () { changeDateWindow(WINDOW_SIZE); });

            if (cancelButton) {
                cancelButton.addEventListener('click', function () {
                    showCancelConfirmationModal();
                });
            }

            if (bookNewAppointmentButton) {
                bookNewAppointmentButton.addEventListener('click', function () {
                    bookingApi.trackEvent('newAppointment');
                    resetBookingFlowToStart();
                });
            }

            if (confirmButton) {
                confirmButton.addEventListener('click', function () {
                    if (!selectedDateStr || !selectedTimeStr) {
                        return;
                    }

                    var reschedulePayload = bookingApi.buildReschedulePayload();
                    if (!reschedulePayload.appointmentId || !reschedulePayload.locationId || !reschedulePayload.date || !reschedulePayload.startTime) {
                        bookingApi.showDateTimeError('Unable to reschedule appointment. Missing required fields.');
                        return;
                    }

                    bookingApi.showLoader();
                    bookingApi.fetchAppointmentReschedule(reschedulePayload)
                        .then(function (response) {
                            if (!response || response.status !== 'confirmed') {
                                throw new Error((response && response.message) || 'Unable to reschedule appointment. Please try again.');
                            }

                            // Build confirmation data from rescheduled appointment response
                            var confirmationData = {
                                appointmentId: response.appointmentId || '',
                                retailerName: response.locationName || '',
                                purpose: response.productName || '',
                                displayDateText: buildDisplayDateText(response.date, response.startTime),
                                location: response.locationName || '',
                                startTimeISO: toCalendarStamp(response.date, response.startTime),
                                endTimeISO: toCalendarStamp(response.date, response.startTime)
                            };
                            
                            bookingApi.confirmationData = confirmationData;
                            bookingApi.renderConfirmationData(confirmationData);

                            updateAppointmentInformationScript(
                                response.productName || '',
                                response.date || '',
                                response.startTime || ''
                            );

                            bookingApi.trackEvent('modifyAppointment');
                            bookingApi.showConfirmation();
                            bookingApi.scrollView('appointmentConfirmationView');
                        })
                        .catch(function (error) {
                            bookingApi.showDateTimeError((error && error.message) || 'Unable to reschedule appointment. Please try again.');
                        })
                        .finally(function () {
                            bookingApi.hideLoader();
                        });
                });
            }
        }

        function showRescheduleContentView() {
            if (rescheduleMainHeader) {
                rescheduleMainHeader.classList.remove('hidden');
            }

            if (rescheduleContentView) {
                rescheduleContentView.classList.remove('hidden');
            }

            if (cancelSuccessView) {
                cancelSuccessView.classList.add('hidden');
            }
        }

        function changeDateWindow(daysOffset) {
            selectedTimeStr = null;
            selectedDateStr = null;
            timeSlotsGrid.innerHTML = '';
            Array.prototype.slice.call(dateGrid.querySelectorAll('.date-tile')).forEach(function (el) { el.classList.remove('selected'); });

            var targetStartDate = new Date(currentStartDate);
            targetStartDate.setDate(targetStartDate.getDate() + daysOffset);

            if (targetStartDate < bookingWindowStartDate && daysOffset < 0) {
                currentStartDate = new Date(bookingWindowStartDate);
            } else if (targetStartDate > bookingWindowEndDate && daysOffset > 0) {
                return;
            } else {
                currentStartDate = targetStartDate;
            }

            renderDateWindow(currentStartDate);
        }

        function formatDateString(dateObj) {
            var year = dateObj.getFullYear();
            var month = String(dateObj.getMonth() + 1).padStart(2, '0');
            var day = String(dateObj.getDate()).padStart(2, '0');
            return year + '-' + month + '-' + day;
        }

        function renderDateWindow(startDate) {
            dateGrid.innerHTML = '';

            var windowDates = [];
            var workingDate = new Date(startDate);

            for (var i = 0; i < WINDOW_SIZE; i++) {
                windowDates.push(new Date(workingDate));
                workingDate.setDate(workingDate.getDate() + 1);
            }

            if (startDate <= bookingWindowStartDate) {
                prevBtn.setAttribute('disabled', 'true');
                prevBtn.style.opacity = '0.3';
                prevBtn.style.cursor = 'not-allowed';
            } else {
                prevBtn.removeAttribute('disabled');
                prevBtn.style.opacity = '1';
                prevBtn.style.cursor = 'pointer';
            }

            var nextWindowCheck = new Date(startDate);
            nextWindowCheck.setDate(nextWindowCheck.getDate() + WINDOW_SIZE);

            if (nextWindowCheck > bookingWindowEndDate) {
                nextBtn.setAttribute('disabled', 'true');
                nextBtn.style.opacity = '0.3';
                nextBtn.style.cursor = 'not-allowed';
            } else {
                nextBtn.removeAttribute('disabled');
                nextBtn.style.opacity = '1';
                nextBtn.style.cursor = 'pointer';
            }

            var firstDate = windowDates[0];
            var lastDate = windowDates[WINDOW_SIZE - 1];

            if (firstDate.getMonth() === lastDate.getMonth()) {
                dateRangeDisplay.textContent = firstDate.getDate() + '-' + lastDate.getDate() + ' ' + monthNames[firstDate.getMonth()] + ' ' + firstDate.getFullYear();
            } else {
                dateRangeDisplay.textContent = firstDate.getDate() + ' ' + monthNames[firstDate.getMonth()] + ' - ' + lastDate.getDate() + ' ' + monthNames[lastDate.getMonth()] + ' ' + firstDate.getFullYear();
            }

            windowDates.forEach(function (date) {
                var dateStr = formatDateString(date);
                var tile = document.createElement('div');
                tile.classList.add('date-tile', 'rolex-v7-fixed16', 'rolex-push-v7--very-small-title', 'pb-2');

                if (date < bookingWindowStartDate || date > bookingWindowEndDate || !availableDatesSet.has(dateStr)) {
                    tile.classList.add('disabled');
                }

                if (selectedDateStr === dateStr) {
                    tile.classList.add('selected');
                }

                tile.innerHTML = '<span class="day-num">' + date.getDate() + '</span>' +
                    '<span class="day-name">' + dayNames[date.getDay()] + '</span>';

                tile.addEventListener('click', function () {
                    if (!tile.classList.contains('disabled')) {
                        Array.prototype.slice.call(dateGrid.querySelectorAll('.date-tile')).forEach(function (el) { el.classList.remove('selected'); });
                        tile.classList.add('selected');
                        selectedDateStr = dateStr;
                        selectedTimeStr = null;
                        loadTimeSlotsForDate(dateStr);
                        validateFormState();
                    }
                });

                dateGrid.appendChild(tile);
            });

            renderTimeSlots(selectedDateStr);
            validateFormState();
        }

        function formatSlotLabel(timeStr) {
            return (timeStr || '').slice(0, 5);
        }

        function loadTimeSlotsForDate(dateStr) {
            selectedTimeStr = null;
            slotsByDate[dateStr] = [];
            bookingApi.selectedRescheduleDateTime = null;
            renderTimeSlots(dateStr);
            validateFormState();

            bookingApi.showLoader();
            bookingApi.loadAppointmentSlotsByContext(dateStr, bookingApi.currentRescheduleContext)
                .then(function (slotResponse) {
                    slotsByDate[dateStr] = (slotResponse && Array.isArray(slotResponse.slots)) ? slotResponse.slots : [];
                    renderTimeSlots(dateStr);
                    validateFormState();
                })
                .catch(function (error) {
                    bookingApi.showDateTimeError((error && error.message) || 'Unable to load time slots. Please try again.');
                })
                .finally(function () {
                    bookingApi.hideLoader();
                });
        }

        function renderTimeSlots(dateStr) {
            timeSlotsGrid.innerHTML = '';

            if (!dateStr) {
                var messageNode = document.getElementById('rescheduleSelectTimeMessageText') || document.getElementById('selectTimeMessageText');
                var value = messageNode ? messageNode.value : '';
                timeSlotsGrid.innerHTML = '<p style="grid-column: span 3; font-size: 13px; color: #8C8280;" class="rolex-v7-fixed16 rolex-push-v7--very-small-title pb-4">' + value + '</p>';
                return;
            }

            var slots = slotsByDate[dateStr] || [];

            if (!slots.length) {
                timeSlotsGrid.innerHTML = '<p style="grid-column: span 3; font-size: 13px; color: #8C8280;" class="rolex-v7-fixed16 rolex-push-v7--very-small-title pb-4">No slots available for selected date.</p>';
                return;
            }

            slots.forEach(function (slot) {
                var timeTile = document.createElement('div');
                timeTile.classList.add('time-tile', 'rolex-v7-fixed16', 'rolex-push-v7--very-small-title');
                timeTile.textContent = formatSlotLabel(slot.startTime);

                if (selectedTimeStr === slot.startTime) {
                    timeTile.classList.add('selected');
                }

                timeTile.addEventListener('click', function () {
                    Array.prototype.slice.call(timeSlotsGrid.querySelectorAll('.time-tile')).forEach(function (el) { el.classList.remove('selected'); });
                    timeTile.classList.add('selected');
                    selectedTimeStr = slot.startTime;
                    bookingApi.selectedRescheduleDateTime = {
                        date: selectedDateStr,
                        startTime: slot.startTime,
                        endTime: slot.endTime || ''
                    };
                    bookingApi.clearDateTimeError();
                    validateFormState();
                });

                timeSlotsGrid.appendChild(timeTile);
            });
        }

        function validateFormState() {
            if (selectedDateStr && selectedTimeStr) {
                confirmButton.removeAttribute('disabled');
            } else {
                confirmButton.setAttribute('disabled', 'true');
            }
        }

        renderDateWindow(currentStartDate);
        setupEventListeners();
    }

    function checkForUrlParmas() {
        var bookingApi = window.AppointmentBooking;
        if (!bookingApi) {
            return;
        }

        var params = new URLSearchParams(window.location.search || '');
        var appointmentId = params.get('appointmentId');
        var cmpid = params.get('cmpid');

        if (appointmentId) {
            bookingApi.showLoader();
            bookingApi.fetchAppointmentById(appointmentId)
                .then(function (appointmentData) {
                    var status = appointmentData.status;
                    if (status.toLowerCase() === 'cancelled') {
                        bookingApi.showDateTimeError('This appointment has been cancelled.');
                        return Promise.reject(new Error('This appointment has been cancelled.'));
                    }

                    return bookingApi.fetchStoresData().then(function (storesResponse) {
                        var storesByState = bookingApi.normalizeStoresByState(storesResponse);
                        var storeLocation;
                        var locationId = appointmentData.locationId || (appointmentData.location && appointmentData.location.id);

                        if (locationId && storesByState) {
                            var stateNames = Object.keys(storesByState);
                            for (var i = 0; i < stateNames.length; i++) {
                                var stateStores = storesByState[stateNames[i]] || [];
                                for (var j = 0; j < stateStores.length; j++) {
                                    var store = stateStores[j];
                                    if (store && store.id === locationId) {
                                        storeLocation = store;
                                        break;
                                    }
                                }
                                if (storeLocation) break;
                            }
                        }

                        var locationText = '';
                        if (storeLocation) {
                            if(storeLocation.formattedAddress) {
                                locationText = storeLocation.formattedAddress;
                            } else {
                                locationText = [storeLocation.address1 || '', storeLocation.city || '', storeLocation.country || ''].filter(Boolean).join(', ');
                            }
                        }

                        var purpose = (appointmentData.product && appointmentData.product.id) === document.querySelector('.discoverVisitProximityId').value ? 'Collection Discovery' : 'Watch Servicing';
                        var confirmationData = {
                            appointmentId: appointmentData.appointmentId,
                            retailerName: storeLocation ? storeLocation.title : appointmentData.locationName,
                            purpose: purpose,
                            displayDateText: buildDisplayDateText(appointmentData.date, appointmentData.startTime),
                            location: locationText || appointmentData.locationName,
                            startTimeISO: toCalendarStamp(appointmentData.date, appointmentData.startTime),
                            endTimeISO: toCalendarStamp(appointmentData.date, appointmentData.endTime)
                        };

                        bookingApi.confirmationData = confirmationData;
                        bookingApi.rescheduleAppointmentData = appointmentData;
                        bookingApi.currentRescheduleContext = {
                            locationId: appointmentData.locationId || (appointmentData.location && appointmentData.location.id) || '',
                            productId: appointmentData.productId || (appointmentData.product && appointmentData.product.id) || '',
                            timezoneId: appointmentData.timezone || appointmentData.timezoneId || (storeLocation && storeLocation.timezoneId) || ''
                        };

                        if (typeof bookingApi.renderConfirmationData === 'function') {
                            bookingApi.renderConfirmationData(confirmationData);
                        }
                        
                        bookingApi.showConfirmation();
                        bookingApi.scrollView('appointmentConfirmationView');
                        if(cmpid === 'email_cancel-appointment') {
                            bookingApi.showCancelAppointmentModal();
                        }
                    });
                })
                .catch(function (error) {
                    if (error && error.message !== 'This appointment has been cancelled.') {
                        bookingApi.showDateTimeError((error && error.message) || 'Unable to retrieve appointment details.');
                    }
                })
                .finally(function () {
                    bookingApi.hideLoader();
                });
        }
    }

    function initInlineViewScripts() {
        initContactInfoView();
        initConfirmationView();
        initDateTimeView();
        initRescheduleView();
        checkForUrlParmas();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initInlineViewScripts);
    } else {
        initInlineViewScripts();
    }
})();
